/**
 * Whether leaving `/konto` now would throw away something the trainer typed (owner, 4 November
 * 2026). Two tabs hold drafts: Profil (the two names, and the password disclosure) and Kolor marki
 * (a picked colour, or a hex still being typed). Switching tabs inside the screen keeps every draft,
 * because the hooks that hold them live on the screen — so this guards leaving the screen, and the
 * screen asks it whichever tab is open.
 *
 * Work is anything the trainer typed that is not what is saved, whether or not it could be saved
 * yet: a half-typed hex and a password that breaks a rule are still typing a person would lose.
 */

import type { ProfileOrigin } from './profilePatch';

export interface AccountDrafts {
  /** What the name fields hold, beside what the trainer's row says. */
  readonly name: { readonly typed: ProfileOrigin; readonly saved: ProfileOrigin };
  /** The two password fields. Cancelling the disclosure clears both. */
  readonly password: { readonly next: string; readonly repeat: string };
  /** A picked colour that differs from the saved one, and a hex field holding a partial value. */
  readonly brand: { readonly changed: boolean; readonly halfTypedHex: boolean };
}

const sameName = (typed: ProfileOrigin, saved: ProfileOrigin): boolean =>
  typed.firstName.trim() === saved.firstName.trim() &&
  typed.lastName.trim() === saved.lastName.trim();

export function leavingLosesWork({ name, password, brand }: AccountDrafts): boolean {
  return (
    !sameName(name.typed, name.saved) ||
    password.next !== '' ||
    password.repeat !== '' ||
    brand.changed ||
    brand.halfTypedHex
  );
}
